import { useEffect } from 'react';

/** Props for the headless PageMeta component. */
interface PageMetaProps {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImageUrl?: string;
  canonicalUrl?: string;
  metaKeywords?: string;
  /** When true, injects <meta name="robots" content="noindex"> to keep this page out of search results (e.g. soft-404s). */
  noIndex?: boolean;
}

/** Updates a single meta attribute imperatively; returns the original value for cleanup. */
function setMeta(selector: string, value: string | undefined, attr: 'content' = 'content'): string {
  const el = document.querySelector(selector) as HTMLMetaElement | null;
  const original = el?.getAttribute(attr) ?? '';
  if (el && value) el.setAttribute(attr, value);
  return original;
}

function safeCanonical(configured: string | undefined): string {
  const pathname = window.location.pathname;
  const knownPaths = ['/', '/privacy', '/accessibility', '/admin', '/admin/preview', '/classic'];
  const fallback = new URL(knownPaths.includes(pathname) ? pathname : '/', 'https://nvision.me').href;
  if (!configured) return fallback;
  try {
    const url = new URL(configured);
    if (url.protocol !== 'https:' || url.username || url.password || !['nvision.me', 'www.nvision.me'].includes(url.hostname)) return fallback;
    url.hostname = 'nvision.me';
    url.hash = '';
    return url.href;
  } catch { return fallback; }
}

/**
 * Headless component that imperatively updates <title>, meta description,
 * OG tags, Twitter cards, canonical URL, and keywords in the document <head>.
 * Restores original values on unmount to prevent leaking between route changes.
 */
export const PageMeta = ({ title, description, ogTitle, ogDescription, ogImageUrl, canonicalUrl, metaKeywords, noIndex }: PageMetaProps) => {
  useEffect(() => {
    // Title
    const originalTitle = document.title;
    document.title = title;

    // Robots (noindex for pages like soft-404s that shouldn't be indexed)
    let robotsEl = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const originalRobots = robotsEl?.getAttribute('content');
    const createdRobots = !robotsEl;
    if (!robotsEl) {
      robotsEl = document.createElement('meta');
      robotsEl.name = 'robots';
      document.head.appendChild(robotsEl);
    }
    robotsEl.content = noIndex ? 'noindex, nofollow' : 'index, follow';

    // Description
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const originalDesc = metaDesc?.content ?? '';
    if (metaDesc) {
      metaDesc.content = description;
    } else {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      metaDesc.content = description;
      document.head.appendChild(metaDesc);
    }

    // OG title — use explicit ogTitle if set, otherwise fall back to title
    const origOgTitle = setMeta('meta[property="og:title"]', ogTitle || title);

    // OG description — use explicit ogDescription if set, otherwise fall back to description
    const origOgDesc = setMeta('meta[property="og:description"]', ogDescription || description);

    // OG image
    const validImage = ogImageUrl && /^https:\/\//.test(ogImageUrl) ? ogImageUrl : undefined;
    const origOgImage = validImage ? setMeta('meta[property="og:image"]', validImage) : '';

    // Twitter meta (keep in sync)
    const origTwitterTitle = setMeta('meta[name="twitter:title"]', ogTitle || title);
    const origTwitterDesc = setMeta('meta[name="twitter:description"]', ogDescription || description);
    const origTwitterImage = validImage ? setMeta('meta[name="twitter:image"]', validImage) : undefined;

    // Canonical URL
    const canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const origCanonical = canonicalEl?.href ?? '';
    const canonical = safeCanonical(canonicalUrl);
    if (canonicalEl) canonicalEl.href = canonical;
    const origOgUrl = setMeta('meta[property="og:url"]', canonical);

    // Meta keywords
    let keywordsEl = document.querySelector('meta[name="keywords"]') as HTMLMetaElement | null;
    const origKeywords = keywordsEl?.content ?? '';
    if (metaKeywords) {
      if (keywordsEl) {
        keywordsEl.content = metaKeywords;
      } else {
        keywordsEl = document.createElement('meta');
        keywordsEl.name = 'keywords';
        keywordsEl.content = metaKeywords;
        document.head.appendChild(keywordsEl);
      }
    }

    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.content = originalDesc;
      setMeta('meta[property="og:title"]', origOgTitle);
      setMeta('meta[property="og:description"]', origOgDesc);
      if (validImage) document.querySelector('meta[property="og:image"]')?.setAttribute('content', origOgImage);
      document.querySelector('meta[property="og:url"]')?.setAttribute('content', origOgUrl);
      document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', origTwitterTitle);
      document.querySelector('meta[name="twitter:description"]')?.setAttribute('content', origTwitterDesc);
      if (origTwitterImage !== undefined) document.querySelector('meta[name="twitter:image"]')?.setAttribute('content', origTwitterImage);
      if (canonicalEl && origCanonical) canonicalEl.href = origCanonical;
      if (keywordsEl) keywordsEl.content = origKeywords;
      if (createdRobots) robotsEl.remove();
      else if (originalRobots === null) robotsEl.removeAttribute('content');
      else robotsEl.setAttribute('content', originalRobots!);
    };
  }, [title, description, ogTitle, ogDescription, ogImageUrl, canonicalUrl, metaKeywords, noIndex]);

  return null;
};
